import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { BuildingId, BuildingOrg, BuildingPreview, OrgId } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { callApi } from 'apiClient';
import { Dialog } from 'ui/shared/containers';
import { TextField } from 'ui/shared/controls';
import { ErrorMessage } from 'ui/shared/messages';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';
import { makeStringAddress } from 'ui/building/settings/address/buildingAddress/BuildingAddress';
import { toError } from 'shared/util/toError';
import { useSession } from 'hooks';

require('./LinkBuildingDialog.css');

export type Props = {
    visible: boolean;
    onLinked(buildingId: BuildingId): void;
    onCancel(): void;
};

export const LinkBuildingDialog = ({ visible, onLinked, onCancel }: Props) => {
    const { t } = useTranslation();
    const { user } = useSession();
    const [ linkCode, setLinkCode ] = useState('');
    const [ isLoading, setLoading ] = useState(false);
    const [ isLinking, setLinking ] = useState(false);
    const [ buildingPreview, setBuildingPreview ] = useState<BuildingPreview>();
    const [ previewError, setPreviewError ] = useState<Error>();
    const [ linkError, setLinkError ] = useState<Error>();
    const [ linkedBuildingId, setLinkedBuildingId ] = useState<BuildingId>();
    
    const linkErrorMessage = linkError && t([ `BUILDINGS.LINK_DIALOG.ERROR.${linkError.message}`, `ERROR.${linkError.message}`, 'SERVER_ERROR' ]);
    const previewErrorMessage = previewError && t([ `BUILDINGS.LINK_DIALOG.ERROR.${previewError.message}`, `ERROR.${previewError.message}`, 'SERVER_ERROR' ]);

    const lookupLinkCode = useCallback((linkCode: string) => {
        setLoading(true);
        setPreviewError(undefined);
        void (async () => {
            try {
                const buildingPreview: BuildingPreview = await callApi('GET', `/api/orgs/link/${linkCode}/preview`);
                setBuildingPreview(buildingPreview);
            }
            catch (e) {
                const err = toError(e);
                setPreviewError(err);
            }
            finally {
                setLoading(false);
            }
        })();
    }, [ setLoading, setPreviewError, setBuildingPreview ]);

    const performLink = useCallback((linkCode: string) => {
        setLinking(true);
        setLinkError(undefined);
        void (async () => {
            try {
                const result: Record<OrgId, BuildingOrg> = await callApi('POST', `/api/buildings/${buildingPreview?.id}/orgs/${user?.orgId}/link/${linkCode}`);
                const buildingOrg = user?.orgId ? result[user!.orgId!] : undefined;
                setLinkedBuildingId(buildingOrg?.buildingId);
            }
            catch (e) {
                const err = toError(e);
                setLinkError(err);
            }
            finally {
                setLinking(false);
            }
        })();
    }, [ setLinking, setLinkError, setLinkedBuildingId, user, buildingPreview?.id ]);

    const handleChangeLinkCode = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value.toUpperCase();
        setLinkCode(value);

        // Automatically send link code to the server to load a building preview
        // once the user has typed all eight characters of the code
        const cleanValue = cleanLinkCode(value);
        if (cleanValue.length === 8) {
            lookupLinkCode(cleanValue);
        }
    }, [ setLinkCode, lookupLinkCode ]);

    // Reset the dialog when it is shown
    useEffect(() => {
        if (visible) {
            setLinkCode('');
            setLoading(false);
            setLinking(false);
            setLinkError(undefined);
            setPreviewError(undefined);
            setLinkedBuildingId(undefined);
            setBuildingPreview(undefined);
        }
    }, [ visible, setLinkCode, setLoading, setLinking, setLinkError, setPreviewError, setLinkedBuildingId, setBuildingPreview ]);

    // Notify the parent once a building has been linked successfully.
    // The parent will then close the dialog window.
    useEffect(() => {
        if (linkedBuildingId) {
            onLinked(linkedBuildingId);
        }
    }, [ linkedBuildingId, onLinked ]);

    return (
        <Dialog
            id="link-building-dialog"
            visible={ visible }
            titleKey="BUILDINGS.LINK_DIALOG.TITLE"
        >
            <label>
                { t('BUILDINGS.LINK_DIALOG.LABEL.LINK_CODE') }
            </label>
            <div className="ui form">
                <TextField
                    id="link-code"
                    maxLength={ 9 /* allow an extra char for the hyphen */ }
                    autoFocus={ true }
                    value={ linkCode }
                    onChange={ handleChangeLinkCode }
                />
                { isLoading &&
                    <LoadingSpinner size="small"/>
                }
            </div>

            <ErrorMessage message={ previewErrorMessage }/>

            { buildingPreview &&
                <div className="_preview">
                    <div>
                        { buildingPreview.displayName }
                    </div>
                    <div>
                        { makeStringAddress(buildingPreview) }
                    </div>
                </div>
            }

            <div style={ { marginTop: '2em' } }>
                <button
                    id="link-button"
                    className="ui primary red button"
                    disabled={ isLoading || isLinking || !buildingPreview }
                    onClick={ () => performLink(cleanLinkCode(linkCode)) }
                >
                    { t('COMMAND.LINK') }
                </button>
                <button
                    id="cancel-link-button"
                    className="ui grey button"
                    disabled={ isLinking }
                    onClick={ onCancel }
                >
                    { t('COMMAND.CANCEL') }
                </button>
            </div>
            <ErrorMessage message={ linkErrorMessage }/>
        </Dialog>
    );
};

function cleanLinkCode(linkCode: string): string {
    return linkCode.replace(/[^a-f0-9]/ig, '');
}
